// Public self-apply page strings — EN + TR.
//
// Used by `features/onboarding/SelfApplyPage.tsx` (/apply, public). Role codes
// (BO2 / SO2 / SO3) are API values and stay in the component; only their
// labels live here. Error text returned by the backend is shown as-is.

import type { LocalizedDict } from '../index'

type SelfApplyRoleCode = 'BO2' | 'SO2' | 'SO3'

type SelfApplyStrings = {
  tagline: string
  roles: Record<SelfApplyRoleCode, string>
  errors: {
    alreadyUnderReview: string
    invalidForm: string
    submissionFailed: (status: number) => string
  }
  success: {
    title: string
    bodyBefore: string
    bodyAfter: string
    backLink: string
  }
  form: {
    title: string
    intro: string
    orgNameLabel: string
    orgNameRequired: string
    emailLabel: string
    emailInvalid: string
    roleLabel: string
    rolePlaceholder: string
    roleRequired: string
    contactNameLabel: string
    websiteLabel: string
    referralLabel: string
    referralPlaceholder: string
    messageLabel: string
    messagePlaceholder: string
    submitting: string
    submit: string
    alreadyInvited: string
    signIn: string
  }
}

export const selfApplyI18n: LocalizedDict<SelfApplyStrings> = {
  en: {
    tagline: 'Apply to join the PEXIFY commerce ecosystem',
    roles: {
      BO2: 'Seller — sell on Etsy/Shopify, fulfilled by PEXIFY',
      SO2: 'Production Partner — produce orders for PEXIFY',
      SO3: 'Vendor — supply stock to the PEXIFY warehouse',
    },
    errors: {
      alreadyUnderReview: 'An application for this email and role is already under review.',
      invalidForm: 'Please check the form — some fields are invalid.',
      submissionFailed: (status) => `Submission failed (${status}).`,
    },
    success: {
      title: 'Application received',
      bodyBefore:
        "Thanks for applying to PEXIFY. Our team will review your application and, if it's a fit, send an invitation to ",
      bodyAfter: ' with the next steps.',
      backLink: 'Back to pexify.net',
    },
    form: {
      title: 'Apply to join PEXIFY',
      intro:
        'Tell us about your business. We review every application and reach out by email with the next steps.',
      orgNameLabel: 'Business / store name',
      orgNameRequired: 'Business name is required.',
      emailLabel: 'Work email',
      emailInvalid: 'Enter a valid email address.',
      roleLabel: 'I want to join as',
      rolePlaceholder: 'Select a role…',
      roleRequired: "Please choose how you'd like to join.",
      contactNameLabel: 'Contact name',
      websiteLabel: 'Website (optional)',
      referralLabel: 'Referral code (optional)',
      referralPlaceholder: 'Got a code from a partner?',
      messageLabel: 'Anything we should know? (optional)',
      messagePlaceholder: 'Tell us about your products, volume, and why PEXIFY is a fit.',
      submitting: 'Submitting…',
      submit: 'Submit application',
      alreadyInvited: 'Already invited?',
      signIn: 'Sign in',
    },
  },
  tr: {
    tagline: 'PEXIFY ticaret ağına katılmak için başvurun',
    roles: {
      BO2: "Satıcı — Etsy/Shopify'da satın, siparişleri PEXIFY gönderir",
      SO2: 'Üretim ortağı — PEXIFY için sipariş üretin',
      SO3: 'Tedarikçi — PEXIFY deposuna stok sağlayın',
    },
    errors: {
      alreadyUnderReview: 'Bu e-posta ve rol için bir başvuru zaten inceleniyor.',
      invalidForm: 'Lütfen formu kontrol edin — bazı alanlar hatalı.',
      submissionFailed: (status) => `Başvuru gönderilemedi (${status}).`,
    },
    success: {
      title: 'Başvurunuz alındı',
      bodyBefore:
        "PEXIFY'a başvurduğunuz için teşekkürler. Ekibimiz başvurunuzu inceleyecek ve uygun görürse sonraki adımlarla birlikte ",
      bodyAfter: ' adresine davet gönderecek.',
      backLink: "pexify.net'e dön",
    },
    form: {
      title: "PEXIFY'a katılmak için başvurun",
      intro:
        'Bize işinizden bahsedin. Her başvuruyu inceliyor, sonraki adımlar için size e-postayla dönüyoruz.',
      orgNameLabel: 'İşletme / mağaza adı',
      orgNameRequired: 'İşletme adı gerekli.',
      emailLabel: 'İş e-postası',
      emailInvalid: 'Geçerli bir e-posta adresi girin.',
      roleLabel: 'Hangi rolle katılmak istiyorsunuz?',
      rolePlaceholder: 'Rol seçin…',
      roleRequired: 'Lütfen nasıl katılmak istediğinizi seçin.',
      contactNameLabel: 'İletişim kişisi',
      websiteLabel: 'Web sitesi (isteğe bağlı)',
      referralLabel: 'Referans kodu (isteğe bağlı)',
      referralPlaceholder: 'Bir iş ortağından kod mu aldınız?',
      messageLabel: 'Bilmemiz gereken bir şey var mı? (isteğe bağlı)',
      messagePlaceholder: "Ürünlerinizden, hacminizden ve PEXIFY'ın size neden uygun olduğundan bahsedin.",
      submitting: 'Gönderiliyor…',
      submit: 'Başvuruyu gönder',
      alreadyInvited: 'Zaten davet aldınız mı?',
      signIn: 'Giriş yap',
    },
  },
}
